import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle, Circle, Share2, ArrowLeft } from "lucide-react";
import Meta from "../../components/Meta";
import Footer from "../../components/Footer";
import Navbar from "../../components/Navbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChecklistNotFound from "./ChecklistNotFound";

const SharedChecklistPage = () => {
  const [checklist, setChecklist] = useState(null);
  const [checklistProgress, setChecklistProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    setLoading(true);
    const hash = location.hash.substring(1); // Remove the #
    if (hash) {
      try {
        const decodedData = JSON.parse(atob(hash));
        // Assign a temporary ID for progress tracking if not present
        if (!decodedData.id) {
          decodedData.id = `shared-${Date.now()}`;
        }
        setChecklist(decodedData);
        // Load progress from localStorage if available for this shared checklist
        const savedProgress = localStorage.getItem(
          `checklist-progress-${decodedData.id}`
        );
        if (savedProgress) {
          setChecklistProgress(JSON.parse(savedProgress));
        }
      } catch (error) {
        console.error("Error decoding checklist data from URL:", error);
        setChecklist(null); // Indicate checklist not found
      }
    }
    setLoading(false);
  }, [location.hash]);

  const toggleItem = (checklistId, itemIndex) => {
    setChecklistProgress((prev) => {
      const newProgress = {
        ...prev,
        [itemIndex]: !prev[itemIndex],
      };
      // Save progress to localStorage for this shared checklist
      localStorage.setItem(
        `checklist-progress-${checklistId}`,
        JSON.stringify(newProgress)
      );
      return newProgress;
    });
  };

  const getProgress = (checklistId, itemsCount) => {
    const completed = Object.values(checklistProgress || {}).filter(
      Boolean
    ).length;
    return Math.round((completed / itemsCount) * 100);
  };

  const shareChecklist = () => {
    // The URL already contains the shareable data
    navigator.clipboard.writeText(window.location.href);
    toast.success("Checklist link copied to clipboard!", {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-gray-700">Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return <ChecklistNotFound />;
  }

  const progress = getProgress(checklist.id, checklist.items.length);
  const completedItems = Object.values(checklistProgress || {}).filter(
    Boolean
  ).length;

  return (
    <>
      <Meta
        title={`${checklist.name} - Govind Sinha & Co.`}
        description={`Shared document checklist: ${checklist.name}`}
        canonical={window.location.href.split("#")[0]} // Canonical to the base URL without hash
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pt-16">
        <Navbar />
        <div className="container max-w-7xl mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Link
                to="/checklists"
                className="p-2 text-gray-600 hover:text-blue-600 rounded-full hover:bg-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900">
                  {checklist.name}
                </h1>
                <p className="text-gray-600 capitalize">
                  {checklist.category} • {checklist.items.length} items
                </p>
              </div>
              <button
                onClick={shareChecklist}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
            </div>

            {/* Progress Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Progress
                </h3>
                <span className="text-2xl font-bold text-blue-600">
                  {progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">
                {completedItems} of {checklist.items.length} items completed
              </p>
            </div>

            {/* Checklist Items */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Documents Required
              </h3>
              <div className="space-y-3">
                {checklist.items.map((item, index) => {
                  const isCompleted = checklistProgress[index] || false;
                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isCompleted
                          ? "bg-green-50 border-green-200 text-green-800"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleItem(checklist.id, index)}
                    >
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-400" />
                      )}
                      <span
                        className={`flex-1 ${
                          isCompleted ? "line-through" : ""
                        }`}
                      >
                        {item}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default SharedChecklistPage;
